import styled from "styled-components";
import useTheme from "../../../../common/hooks/useTheme";
import { forwardRef, ReactNode } from "react";

// The horizontal twin of ScrollableWrapper: a row that scrolls sideways with
// the same thin, inset, non-overlay scrollbar (8px track, 4px rounded thumb in
// the neutral border colour, transparent track). Use it for tab strips and
// card rails whose content may be wider than the page; the bar only appears
// when the content overflows, and the bottom padding keeps it clear of the
// content when it does.
//
// The scrollbar rules follow ScrollableWrapper exactly, for the same reasons
// (see its comments): no top-level scrollbar-width / scrollbar-color, since in
// Chrome 121+ those switch the browser to its overlay bar and discard the
// ::-webkit-scrollbar rules; Firefox gets the standard properties behind the
// @supports (-moz-appearance) guard, the only query that is true in Firefox
// alone.
const Wrapper = styled.div<{ $thumb: string; $gap: string; $stretch: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: ${({ $stretch }) => ($stretch ? "stretch" : "flex-start")};
  gap: ${({ $gap }) => $gap};
  min-width: 0;
  max-width: 100%;
  padding-bottom: 4px;

  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;

  /* Children keep their natural width and overflow into the scroller; as flex
     items they would otherwise shrink and wrap their text instead. */
  & > * {
    flex-shrink: 0;
  }

  @supports (-moz-appearance: none) {
    scrollbar-width: thin;
    scrollbar-color: ${({ $thumb }) => $thumb} transparent;
  }

  &::-webkit-scrollbar {
    height: 8px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ $thumb }) => $thumb};
    border-radius: 10px;
    border: 2px solid transparent;
    background-clip: padding-box;
  }
`;

const HorizontalScrollableWrapper = forwardRef<
  HTMLDivElement,
  {
    children: ReactNode;
    /** Space between the children. Defaults to none (a tab strip). */
    gap?: string;
    /** Stretch the children to the tallest one (a rail of cards). */
    stretch?: boolean;
  }
>(({ children, gap = "0", stretch = false }, ref) => {
  const theme = useTheme();
  return (
    <Wrapper
      ref={ref}
      $thumb={theme.colors_ds.NEUTRAL_BORDER_DEFAULT}
      $gap={gap}
      $stretch={stretch}
    >
      {children}
    </Wrapper>
  );
});

export default HorizontalScrollableWrapper;
